import { motion } from 'framer-motion'

interface Option<T extends string> {
  value: T
  label: string
  emoji?: string
}

interface MultiSelectProps<T extends string> {
  options: Option<T>[]
  values: T[]
  onChange: (values: T[]) => void
  columns?: 2 | 3
  max?: number
}

export function MultiSelect<T extends string>({
  options,
  values,
  onChange,
  columns = 2,
  max,
}: MultiSelectProps<T>) {
  const toggleOption = (optionValue: T) => {
    if (optionValue === 'none' as T) {
      onChange(['none' as T])
      return
    }

    const withoutNone = values.filter(v => v !== 'none' as T)

    if (withoutNone.includes(optionValue)) {
      onChange(withoutNone.filter((v) => v !== optionValue))
    } else {
      if (max && withoutNone.length >= max) return
      onChange([...withoutNone, optionValue])
    }
  }

  return (
    <div className={`grid gap-3 ${columns === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
      {options.map((option, index) => {
        const isSelected = values.includes(option.value)

        return (
          <motion.button
            key={option.value}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => toggleOption(option.value)}
            className={`
              relative flex flex-col items-center justify-center
              min-h-[100px] px-3 py-4 rounded-xl transition-all duration-200
              border-2
              ${isSelected
                ? 'border-primary bg-teal-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
              }
            `}
          >
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center"
              >
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
            )}
            {option.emoji && (
              <span className="text-3xl mb-2">{option.emoji}</span>
            )}
            <span className={`font-medium text-sm text-center ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
              {option.label}
            </span>
          </motion.button>
        )
      })}
    </div>
  )
}
